import React, { useState, useEffect } from "react";
import {
  Drawer,
  Empty,
  Flex,
  Spin,
  Table,
} from "antd";
import type { TableColumnsType } from "antd";
import { useNavigate } from "react-router";
import mainDashboardApis from "../../api/dashboard/MainDashboard";

interface PRSummaryCategoryDrawerProps {
  visible: boolean;
  onClose: () => void;
  categoryId: string;
  siteId?: string;
  startDate?: string;
  endDate?: string;
}

const PRSummaryCategoryDrawer: React.FC<PRSummaryCategoryDrawerProps> = ({
  visible,
  onClose,
  categoryId,
  siteId,
  startDate,
  endDate,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryData, setCategoryData] = useState<any[]>([]);

  useEffect(() => {
    if (visible && categoryId) {
      fetchCategoryList();
    }
  }, [visible, categoryId, siteId, startDate, endDate]);

  const fetchCategoryList = async () => {
    setIsLoading(true);
    try {
      const requestBody: any = {
        categoryId: categoryId,
      };

      // Add optional parameters if provided
      if (siteId) {
        requestBody.siteId = siteId;
      }
      if (startDate) {
        requestBody.startDate = startDate;
      }
      if (endDate) {
        requestBody.endDate = endDate;
      }

      const res = await mainDashboardApis.prSummaryCategoryList(requestBody);
      if (res?.status && res?.response?.data) {
        setCategoryData(res.response.data);
      } else {
        setCategoryData([]);
      }
    } catch (error) {
      console.error("Error fetching category list:", error);
      setCategoryData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: TableColumnsType<any> = [
    {
      title: "PR Sequence",
      dataIndex: "prSequence",
      key: "prSequence",
      width: 120,
      render: (text: any, record: any) => {
        if (!text) return <span>-</span>;
        return (
          <span
            className="inlineTableCellLink"
            style={{ cursor: "pointer", color: "#2263b3" }}
            onClick={() => {
              if (record.prId) {
                navigate(`/view_pr/${record.prId}`, {
                  state: { 
                    previousPath: "/pr-summary-details",
                    openDrawer: true,
                    categoryId: categoryId,
                    startDate: startDate,
                    endDate: endDate,
                  },
                });
              }
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Site",
      dataIndex: "site",
      key: "site",
      width: 200,
    },
    {
      title: "Requested By",
      dataIndex: "reqiestedBY",
      key: "reqiestedBY",
      width: 150,
    },
    {
      title: "Requested Date",
      dataIndex: "RequestedDate",
      key: "RequestedDate",
      width: 150,
      render: (text: string) => {
        if (!text) return "-";
        const date = new Date(text);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      title: "Need By Date",
      dataIndex: "NeedByDate",
      key: "NeedByDate",
      width: 150,
      render: (text: string) => {
        if (!text) return "-";
        const date = new Date(text);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      title: "Days to Delivery",
      dataIndex: "Days to Delivery",
      key: "Days to Delivery",
      width: 150,
      render: (text: string) => {
        if (!text) return "-";
        const isOverdue = text.includes("overdue");
        return (
          <span style={{ color: isOverdue ? "#ff4d4f" : "#52c41a" }}>
            {text}
          </span>
        );
      },
    },
  ];

  return (
    <Drawer
      title="Purchase Request Summary - Category Details"
      placement="right"
      onClose={onClose}
      open={visible}
      width="85%"
    >
      {isLoading ? (
        <Flex align="center" gap="middle" className="pageLoader">
          <Spin size="large" className="m-auto" />
        </Flex>
      ) : categoryData.length === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Empty description="No Data Available" />
        </div>
      ) : (
        <Table
          className="customTable"
          columns={columns}
          dataSource={categoryData}
          rowKey={(record) => `pr-${record.prId}-${record.siteId}`}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30", "50"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          bordered
          size="middle"
          scroll={{ x: 1200 }}
        />
      )}
    </Drawer>
  );
};

export default PRSummaryCategoryDrawer;

